import type { getWorkspaceSessionSnapshot } from '@/lib/session/workspace-session'
import type { QueryClient } from '@tanstack/react-query'
import { createLogger } from '@/lib/logger'
import { useOrganizationStore } from '@/store/organization.store'
import { useProjectFiltersStore } from '@/store/project-filters.store'

const logger = createLogger('lib/prefetch/navigationData')

/** Intent-only reads; never prefetch financial, administration or permission data. */
export async function prefetchNavigationData(
  path: string,
  client: QueryClient,
  scope: ReturnType<typeof getWorkspaceSessionSnapshot>,
): Promise<void> {
  if (
    !scope.enabled ||
    !scope.ownerUserId ||
    !scope.organizationId ||
    navigator.onLine === false
  )
    return
  const pathname = path.split(/[?#]/)[0]
  const slug = useOrganizationStore.getState().currentOrganization?.slug
  const route =
    slug && pathname.startsWith(`/${slug}/`)
      ? pathname.slice(slug.length + 1)
      : pathname
  try {
    scope.assertUnchanged()
    if (route === '/tasks/mine') {
      const { myTasksQueryOptions } = await import('@/modules/tasks/navigation')
      scope.assertUnchanged()
      const options = myTasksQueryOptions({
        userId: scope.ownerUserId,
        organizationId: scope.organizationId,
        includeCompleted: true,
      })
      await client.prefetchQuery({
        ...options,
        queryFn: ({ signal }) =>
          scope.run(signal, signal => options.queryFn({ signal })),
      })
    } else if (route === '/projects') {
      const { projectsListQueryOptions } =
        await import('@/modules/projects/navigation')
      scope.assertUnchanged()
      await client.prefetchQuery(
        projectsListQueryOptions(
          {
            ...useProjectFiltersStore.getState().filters,
            page: 1,
            limit: 500,
            isTemplate: false,
            query: undefined,
          },
          scope,
        ),
      )
    } else if (route === '/tasks') {
      // Reuse the most recent registered list's exact saved-view filters and
      // query function. Do not guess at a different page or fetch every view.
      const query = client
        .getQueryCache()
        .findAll({ queryKey: ['tasks'] })
        .filter(
          query =>
            query.queryKey[1] === 'ORGANIZATION' &&
            query.queryKey[2] === scope.organizationId &&
            query.queryKey.includes('source:api'),
        )
        .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0]
      const fetch = query?.options.queryFn
      if (query && typeof fetch === 'function')
        await client.prefetchQuery({
          queryKey: query.queryKey,
          queryFn: context =>
            scope.run(context.signal, async signal =>
              fetch({ ...context, signal }),
            ),
          staleTime: 30_000,
        })
    }
  } catch (error) {
    // Hovering must stay silent offline or when the workspace changes.
    logger.debug('Navigation prefetch unavailable:', error)
  }
}
